import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch, cachedGet, cachedGetOr } from '../utils/api';

export default function Leaderboards() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null); // null = global
  const [leaderboard, setLeaderboard] = useState({ players: [], totals: {}, pagination: {} });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef();
  const loadMoreRef = useRef();

  useEffect(() => {
    fetchGames();
  }, []);

  useEffect(() => {
    fetchLeaderboard(true);
  }, [selectedGame]);

  const fetchGames = async () => {
    const { data } = await cachedGetOr('/api/games', []);
    setGames(data || []);
  };

  const fetchLeaderboard = async (reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : leaderboard.players.length;
      const endpoint = selectedGame
        ? `/api/scores/leaderboard/${selectedGame.id}?limit=20&offset=${offset}`
        : `/api/scores/leaderboard?limit=20&offset=${offset}`;

      // Only the first page falls back to cache. Replaying a cached page while
      // appending would duplicate rows already on screen.
      let data;
      if (reset) {
        ({ data } = await cachedGet(endpoint));
      } else {
        const res = await apiFetch(endpoint);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        data = await res.json();
      }

      if (reset) {
        setLeaderboard(data);
      } else {
        setLeaderboard(prev => ({
          ...data,
          players: [...prev.players, ...data.players]
        }));
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Infinite scroll observer
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && leaderboard.pagination?.hasMore && !loadingMore) {
      fetchLeaderboard(false);
    }
  }, [leaderboard.pagination?.hasMore, loadingMore]);

  useEffect(() => {
    const option = { root: null, rootMargin: '20px', threshold: 0 };
    observerRef.current = new IntersectionObserver(handleObserver, option);
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  const getRankBadge = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `#${index + 1}`;
  };

  const stats = [
    { label: 'Total Games', value: leaderboard.totals?.total_games || 0, icon: '🎮' },
    { label: 'Total Players', value: leaderboard.totals?.total_players || 0, icon: '👥' },
    { label: 'Total Points', value: (leaderboard.totals?.total_points || 0).toLocaleString(), icon: '📊' },
    { label: selectedGame ? 'Best Score' : 'Total Wins', value: selectedGame ? (leaderboard.totals?.best_score_ever || '—') : (leaderboard.totals?.total_wins || 0), icon: selectedGame ? '🏆' : '🥇' },
    { label: 'Avg Score', value: leaderboard.totals?.avg_score_all || '—', icon: '📈', hidden: !selectedGame },
    { label: 'Players Ranked', value: leaderboard.totals?.total_entries || 0, icon: '🎯' },
  ].filter(s => !s.hidden);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white"
          >
            ← Back
          </button>
          <div className="flex-1 text-center">
            <h1 className="text-2xl font-bold text-white">🥇 Leaderboards</h1>
            <p className="text-slate-400 text-sm">Who's the best?</p>
          </div>
          <div className="w-10" />
        </div>

        {/* Game Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedGame(null)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              !selectedGame
                ? 'bg-white text-slate-900'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            All Games
          </button>
          {games.map(game => (
            <button
              key={game.id}
              onClick={() => setSelectedGame(game)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                selectedGame?.id === game.id
                  ? 'bg-white text-slate-900'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <span>{game.emoji}</span>
              <span>{game.name}</span>
            </button>
          ))}
        </div>

        {/* Hero Stats Card */}
        <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            {selectedGame ? (
              <><span>{selectedGame.emoji}</span> {selectedGame.name} Stats</>
            ) : (
              <>📊 Global Stats</>
            )}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.slice(0, 8).map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl mb-1">{stat.icon}</div>
                <div className="text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-xs text-slate-400 uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Leaderboard Table */}
        <div className="bg-slate-800/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h2 className="font-semibold text-white">Rankings</h2>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : leaderboard.players.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              No games played yet. Be the first!
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-700/50 text-xs text-slate-400 uppercase">
                <div className="col-span-1">Rank</div>
                <div className="col-span-4">Player</div>
                <div className="col-span-2 text-center">Games</div>
                <div className="col-span-2 text-center">Wins</div>
                <div className="col-span-2 text-center">Avg Score</div>
                <div className="col-span-1 text-center">{selectedGame ? 'Best' : 'Win %'}</div>
              </div>

              {/* Table Rows */}
              {leaderboard.players.map((player, index) => (
                <div
                  key={player.id}
                  onClick={() => navigate(`/profile/${player.id}`)}
                  className={`grid grid-cols-12 gap-2 px-4 py-3 hover:bg-white/5 cursor-pointer transition-colors ${
                    player.id === user?.id ? 'bg-blue-500/10 border-l-2 border-blue-500' : ''
                  }`}
                >
                  <div className="col-span-1 flex items-center">
                    <span className={`font-bold ${index < 3 ? 'text-xl' : 'text-slate-400'}`}>
                      {getRankBadge(index)}
                    </span>
                  </div>
                  <div className="col-span-4 flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm"
                      style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                    >
                      {player.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-white font-medium">
                        {player.name}
                        {player.id === user?.id && <span className="text-blue-400 text-xs ml-1">(you)</span>}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center justify-center text-slate-300">
                    {player.games_played}
                  </div>
                  <div className="col-span-2 flex items-center justify-center">
                    <span className="text-green-400 font-semibold">{player.wins}</span>
                  </div>
                  <div className="col-span-2 flex items-center justify-center text-slate-300">
                    {player.avg_score}
                  </div>
                  <div className="col-span-1 flex items-center justify-center text-slate-300">
                    {selectedGame ? player.best_score : `${player.win_rate || 0}%`}
                  </div>
                </div>
              ))}

              {/* Load More Indicator */}
              <div ref={loadMoreRef} className="p-4 text-center">
                {loadingMore && <span className="text-slate-400">Loading more...</span>}
                {!loadingMore && leaderboard.pagination?.hasMore && (
                  <span className="text-slate-500 text-sm">Scroll for more</span>
                )}
                {!leaderboard.pagination?.hasMore && leaderboard.players.length > 0 && (
                  <span className="text-slate-500 text-sm">End of leaderboard</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
